import { FC, ReactNode, useEffect } from 'react';
import { TOKENS } from './tokens';

type Tone = 'action' | 'danger';

type Props = {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ConfirmDialog: FC<Props> = ({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'action',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  // Close on Escape without giving the page a chance to scroll behind the
  // open modal — same shape used by other ad-hoc dropdowns in the codebase.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel();
    };
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, loading, onCancel]);

  if (!open) return null;

  const confirmBg = tone === 'danger' ? TOKENS.color.severity.high.text : TOKENS.color.action;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onCancel(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17, 24, 39, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: TOKENS.spacing.lg,
      }}
    >
      <div
        role='dialog'
        aria-modal='true'
        style={{
          width: '100%',
          maxWidth: 420,
          background: TOKENS.color.card,
          borderRadius: TOKENS.radius.card,
          boxShadow: '0 20px 50px rgba(17, 24, 39, 0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px 22px 16px', borderBottom: `1px solid ${TOKENS.color.border}` }}>
          <h3 style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 700,
            color: TOKENS.color.text,
          }}>{title}</h3>
          {description && (
            <div style={{
              marginTop: TOKENS.spacing.sm,
              fontSize: 13,
              lineHeight: 1.55,
              color: TOKENS.color.muted,
            }}>
              {description}
            </div>
          )}
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: TOKENS.spacing.sm,
          padding: '14px 22px',
          background: TOKENS.color.surface,
        }}>
          <button
            type='button'
            onClick={onCancel}
            disabled={loading}
            style={{
              border: `1px solid ${TOKENS.color.border}`,
              background: TOKENS.color.card,
              color: TOKENS.color.text,
              padding: '8px 14px',
              borderRadius: TOKENS.radius.chip,
              fontSize: 13,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type='button'
            onClick={onConfirm}
            disabled={loading}
            style={{
              border: 'none',
              background: confirmBg,
              color: '#fff',
              padding: '8px 14px',
              borderRadius: TOKENS.radius.chip,
              fontSize: 13,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              minWidth: 90,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            {loading ? 'Aguarde…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
